import { Fragment, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Paper,
  Stack,
  Switch,
  TextareaAutosize,
  TextField,
  Typography,
} from '@mui/material';

import { LayoutQuestion } from '../LayoutQuestion';

import styles from '../../Form/Form.module.css';

const FILE_TYPE_OPTIONS = [
  { value: 'txt', label: 'Texto (.txt)', icon: '📄' },
  { value: 'csv', label: 'CSV (.csv)', icon: '📊' },
  { value: 'xlsx', label: 'Excel (.xlsx, .xls)', icon: '📗' },
  { value: 'pdf', label: 'PDF (.pdf)', icon: '📕' },
  { value: 'docx', label: 'Word (.docx, .doc)', icon: '📘' },
  { value: 'pptx', label: 'PowerPoint (.pptx, .ppt)', icon: '📙' },
  { value: 'img', label: 'Imagen (.jpg, .jpeg, .png, .gif, .svg)', icon: '🖼️' },
];

const MAX_FILE_SIZES = [1, 2, 5, 10];

export default function FileUploadQuestion(props) {
  const [showPreview, setShowPreview] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized) {
      const needsDefaults =
        !props.information.customOptions ||
        !props.information.limitOptions ||
        !props.information.barBipolarValue;

      if (needsDefaults) {
        props.setInformation({
          ...props.information,
          customOptions: ['pdf'],
          limitOptions: props.information.limitOptions || 3,
          barBipolarValue: props.information.barBipolarValue || 5,
        });
      }
      setInitialized(true);
    }
  }, [initialized]);

  const allowedFileTypes = (props.information.customOptions || []).filter(
    (type) => type && type.trim() !== ''
  );
  const finalAllowedTypes = allowedFileTypes.length > 0 ? allowedFileTypes : [];
  const maxFiles = props.information.limitOptions || 3;
  const maxFileSize = props.information.barBipolarValue || 5;

  const handleTypeToggle = (typeValue) => {
    const exists = finalAllowedTypes.includes(typeValue);
    if (exists && finalAllowedTypes.length === 1) return; // al menos uno
    const newTypes = exists
      ? finalAllowedTypes.filter((t) => t !== typeValue)
      : [...finalAllowedTypes, typeValue];
    props.setInformation({ ...props.information, customOptions: newTypes });
  };

  const handleMaxFilesChange = (value) => {
    const limited = Math.max(1, Math.min(5, parseInt(value) || 1));
    props.setInformation({ ...props.information, limitOptions: limited });
  };

  const handleMaxFileSizeChange = (value) => {
    props.setInformation({ ...props.information, barBipolarValue: value });
  };

  const getFileTypeLabels = () => {
    if (finalAllowedTypes.length === 0) return 'Ninguno seleccionado';
    return finalAllowedTypes
      .map((type) => {
        const ft = FILE_TYPE_OPTIONS.find((o) => o.value === type);
        return ft ? ft.label : type;
      })
      .join(', ');
  };

  return (
    <Fragment>
      <LayoutQuestion {...props}>


        {/* ── Configuración estilo Edit ── */}
        <Box sx={{ mb: 2, mt: 2 }}>
          <Stack spacing={3}>

            {/* Tipos de archivo */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                border: finalAllowedTypes.length === 0 ? '2px solid #d32f2f' : 'none',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    p: 1,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <InsertDriveFileIcon />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Tipos de Archivo Permitidos *
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Selecciona los formatos que los usuarios pueden subir
                  </Typography>
                </Box>
              </Box>

              <FormControl
                component="fieldset"
                error={finalAllowedTypes.length === 0}
                sx={{ width: '100%' }}
              >
                <FormGroup>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                      },
                      gap: 2,
                    }}
                  >
                    {FILE_TYPE_OPTIONS.map((option) => {
                      const selected = finalAllowedTypes.includes(option.value);
                      return (
                        <FormControlLabel
                          key={option.value}
                          control={
                            <Switch
                              checked={selected}
                              onChange={() => handleTypeToggle(option.value)}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span style={{ fontSize: '1.2rem' }}>{option.icon}</span>
                              <Typography variant="body2">{option.label}</Typography>
                            </Box>
                          }
                          sx={{
                            border: '1px solid',
                            borderColor: selected ? 'primary.main' : 'grey.300',
                            borderRadius: 2,
                            p: 1.5,
                            m: 0,
                            bgcolor: selected ? 'primary.50' : 'white',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: 'primary.50',
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                </FormGroup>
                {finalAllowedTypes.length === 0 && (
                  <FormHelperText sx={{ mt: 1, fontSize: '0.875rem' }}>
                    Debes seleccionar al menos un tipo de archivo
                  </FormHelperText>
                )}
              </FormControl>

              {finalAllowedTypes.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: 'block' }}
                  >
                    Tipos seleccionados ({finalAllowedTypes.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {finalAllowedTypes.map((type) => {
                      const option = FILE_TYPE_OPTIONS.find((o) => o.value === type);
                      return (
                        <Chip
                          key={type}
                          label={option?.label ?? type.toUpperCase()}
                          onDelete={() => handleTypeToggle(type)}
                          deleteIcon={<CloseIcon />}
                          color="primary"
                          variant="filled"
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Límites de carga */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    bgcolor: 'secondary.main',
                    color: 'white',
                    p: 1,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CloudUploadIcon />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Límites de Carga
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Define el tamaño máximo y cantidad de archivos
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 3,
                }}
              >
                {/* Tamaño máximo — select nativo → TextField number igual que edit */}
                <TextField
                  label="Tamaño máximo por archivo (MB)"
                  type="number"
                  value={maxFileSize}
                  onChange={(e) => handleMaxFileSizeChange(parseInt(e.target.value) || 5)}
                  helperText="Opciones: 1 a 5 MB"
                  InputProps={{ inputProps: { min: 1, max: 5 } }}
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                {/* Cantidad máxima */}
                <TextField
                  label="Cantidad máxima de archivos"
                  type="number"
                  value={maxFiles}
                  onChange={(e) => handleMaxFilesChange(e.target.value)}
                  helperText="Límite: 1-5 archivos"
                  InputProps={{ inputProps: { min: 1, max: 5 } }}
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
            </Paper>

            {/* Resumen */}
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Configuración actual:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                • Los usuarios podrán subir hasta <strong>{maxFiles}</strong>{' '}
                {maxFiles === 1 ? 'archivo' : 'archivos'}
              </Typography>
              <Typography variant="body2">
                • Tamaño máximo por archivo: <strong>{maxFileSize} MB</strong>
              </Typography>
              <Typography variant="body2">
                • Formatos permitidos: <strong>{finalAllowedTypes.length}</strong>{' '}
                {finalAllowedTypes.length === 1 ? 'tipo' : 'tipos'}
              </Typography>
            </Alert>

            {/* Vista previa */}
            <Box>
              <Button
                variant="outlined"
                startIcon={showPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
                onClick={() => setShowPreview(!showPreview)}
                size="small"
              >
                {showPreview ? 'Ocultar vista previa' : 'Mostrar vista previa'}
              </Button>
            </Box>

            <Collapse in={showPreview}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                  border: '2px dashed #ddd',
                  borderRadius: '8px',
                }}
              >
                <UploadFileIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                  Arrastra y suelta{' '}
                  {maxFiles === 1 ? 'tu archivo' : 'tus archivos'} aquí
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  o haz clic para seleccionar
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<InsertDriveFileIcon />}
                  disabled
                  sx={{ mb: 2 }}
                >
                  Seleccionar {maxFiles === 1 ? 'archivo' : 'archivos'}
                </Button>

                <Stack spacing={1} sx={{ mt: 2 }}>
                  {finalAllowedTypes.length > 0 && (
                    <Alert severity="info" sx={{ textAlign: 'left' }}>
                      <strong>Archivos permitidos:</strong> {getFileTypeLabels()}
                    </Alert>
                  )}
                  <Alert severity="info" sx={{ textAlign: 'left' }}>
                    <strong>Cantidad máxima:</strong> {maxFiles}{' '}
                    {maxFiles === 1 ? 'archivo' : 'archivos'}
                  </Alert>
                  <Alert severity="info" sx={{ textAlign: 'left' }}>
                    <strong>Tamaño máximo por archivo:</strong> {maxFileSize} MB
                  </Alert>
                </Stack>
              </Paper>

              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 1, color: '#666' }}
              >
                Esta es solo una vista previa. Los usuarios podrán cargar
                archivos reales al responder la encuesta.
              </Typography>
            </Collapse>

          </Stack>
        </Box>
      </LayoutQuestion>
    </Fragment>
  );
}